/**
 * ENTITLEMENT FLAGS (B1 · `docs/plans/01-titularidad.md`).
 *
 * Six switches, one per stage reversible on its own. The rule that orders them:
 * **`academy.entitlement` off ⇒ the server blocks no one**. It's not a
 * nuance: it's the factory default. Turning on the vertical must not change
 * anyone's behavior until an administrator decides a policy — a money
 * feature that activates itself is an incident.
 *
 * What these flags are NOT: a feature flag is a uniform product decision
 * per (academy, course); entitlement is a decision **per user and per
 * moment**. That's why entitlement doesn't live in `feature_flags` (it would
 * need a row per student) and why the `sync_scoping` flag **doesn't trim any
 * stream**: it gates what the materializer writes. Trimming streams by feature
 * would break local-first without gaining any security.
 *
 * `dataPolicy: "preserved"` with a nuance that must be said: on the SERVER
 * nothing is ever deleted, but scoping the replica **does** remove rows from
 * the student's device on reconnect. The `flowsBlocked` text says so, so the
 * confirmation dialog doesn't lie.
 */

import type { FeatureManifest } from "../country/packs";

const ENTITLEMENT: FeatureManifest = {
  key: "academy.entitlement",
  name: "Titularidad (qué pasa si no ha pagado)",
  description:
    "Decide qué ocurre cuando un alumno no ha pagado, con herencia en cascada: plataforma → academia → programa → curso → cohorte → alumno concreto. Apagada, nadie se bloquea: el servidor calcula y explica, pero no retira nada.",
  scope: "org",
  status: "beta",
  gateable: true,
  // Born OFF: a money feature that activates itself is an incident.
  defaultEnabled: false,
  since: "2026-07",
  docsSlug: "caja",
  // SOFT edge: entitlement relies on billing to know who owes, but it isn't
  // held hostage by it — an academy can have the billing vertical off and
  // still want the calculation off and explainable.
  requires: ["billing.core?"],
  implications: {
    navHidden: ["Titularidad (/academy/entitlement)"],
    flowsBlocked: [
      "Aplicar la política de impago (nadie queda bloqueado)",
      "Materializar el estado de titularidad de los alumnos",
    ],
    dataPolicy: "preserved",
  },
};

const ENTITLEMENT_POLICIES: FeatureManifest = {
  key: "academy.entitlement.policies",
  name: "Políticas de titularidad",
  description:
    "Edición de la política por nivel (plataforma, academia, programa, curso, cohorte): días de gracia, umbral mínimo de deuda, ventana para pagos pendientes de verificar y qué se retira. Una política «cerrada» impide que los niveles más específicos la aflojen.",
  scope: "org",
  status: "beta",
  gateable: true,
  // Born OFF: a money feature that activates itself is an incident.
  defaultEnabled: false,
  since: "2026-07",
  docsSlug: "caja",
  requires: ["academy.entitlement"],
  implications: {
    navHidden: [],
    flowsBlocked: ["Crear, editar y borrar políticas de titularidad"],
    dataPolicy: "preserved",
  },
};

const ENTITLEMENT_REST_GATES: FeatureManifest = {
  key: "academy.entitlement.rest_gates",
  name: "Bloqueo de grabaciones, clase en vivo y certificado",
  description:
    "Los tres activos que el servidor sirve frescos y que, por tanto, son REALMENTE revocables. Apagado, la titularidad se calcula y se explica pero no muerde: es el modo «observar antes de aplicar».",
  scope: "org",
  status: "beta",
  gateable: true,
  // Born OFF: a money feature that activates itself is an incident.
  defaultEnabled: false,
  since: "2026-07",
  docsSlug: "caja",
  requires: ["academy.entitlement"],
  implications: {
    navHidden: [],
    flowsBlocked: [
      "Retener la reproducción de grabaciones a un alumno moroso",
      "Retener el enlace de la clase en vivo a un alumno moroso",
      "Retener la emisión del certificado a un alumno moroso",
    ],
    dataPolicy: "preserved",
  },
};

const ENTITLEMENT_WRITE_GATES: FeatureManifest = {
  key: "academy.entitlement.write_gates",
  name: "Solo lectura para morosos",
  description:
    "Aplica el modo «solo lectura» a las entregas y al foro. La nota y la asistencia YA obtenidas no se tocan nunca: el expediente académico no se retiene por deuda.",
  scope: "org",
  status: "beta",
  gateable: true,
  // Born OFF: a money feature that activates itself is an incident.
  defaultEnabled: false,
  since: "2026-07",
  docsSlug: "caja",
  requires: ["academy.entitlement"],
  implications: {
    navHidden: [],
    flowsBlocked: [
      "Impedir entregas de tareas a un alumno moroso",
      "Impedir publicar en el foro a un alumno moroso",
    ],
    dataPolicy: "preserved",
  },
};

const ENTITLEMENT_STUDENT_EXCEPTIONS: FeatureManifest = {
  key: "academy.entitlement.student_exceptions",
  name: "Excepciones por alumno",
  description:
    "Perdonar el bloqueo a un alumno concreto, con motivo y fecha de caducidad OBLIGATORIOS. Solo el dueño o un administrador de la academia pueden concederlas, y cada concesión y cada revocación quedan auditadas.",
  scope: "org",
  status: "beta",
  gateable: true,
  // Born OFF: a money feature that activates itself is an incident.
  defaultEnabled: false,
  since: "2026-07",
  docsSlug: "caja",
  requires: ["academy.entitlement.policies"],
  implications: {
    navHidden: [],
    flowsBlocked: ["Conceder y revocar excepciones de titularidad a un alumno"],
    dataPolicy: "preserved",
  },
};

const ENTITLEMENT_SYNC_SCOPING: FeatureManifest = {
  key: "academy.entitlement.sync_scoping",
  name: "Acotar la réplica por titularidad",
  description:
    "Acota qué contenido se replica al dispositivo según la titularidad. NO recorta ningún stream por feature: cambia lo que el servidor escribe en la tabla de cursos con derecho, así que encenderlo o apagarlo no despliega nada y se revierte en un ciclo de recálculo.",
  scope: "platform",
  status: "beta",
  defaultEnabled: false,
  // NOT `gateable`: it doesn't gate any WRITE. It decides what the
  // materializer projects, which is a projection decision and not a barrier —
  // marking it gateable would force inventing a nonexistent `feature_gate`.
  since: "2026-07",
  docsSlug: "caja",
  requires: ["academy.entitlement"],
  implications: {
    navHidden: [],
    flowsBlocked: ["Retirar del dispositivo del alumno el árbol del curso impagado al reconectar"],
    dataPolicy: "preserved",
  },
};

/** Entitlement manifests (source `entitlement`). */
export function entitlementManifests(): FeatureManifest[] {
  return [
    ENTITLEMENT,
    ENTITLEMENT_POLICIES,
    ENTITLEMENT_REST_GATES,
    ENTITLEMENT_WRITE_GATES,
    ENTITLEMENT_STUDENT_EXCEPTIONS,
    ENTITLEMENT_SYNC_SCOPING,
  ];
}

/** Greppable keys from the client (mirror of `platform::features::keys`). */
export const FLAG_ENTITLEMENT = ENTITLEMENT.key;
export const FLAG_ENTITLEMENT_POLICIES = ENTITLEMENT_POLICIES.key;
export const FLAG_ENTITLEMENT_REST_GATES = ENTITLEMENT_REST_GATES.key;
export const FLAG_ENTITLEMENT_WRITE_GATES = ENTITLEMENT_WRITE_GATES.key;
export const FLAG_ENTITLEMENT_STUDENT_EXCEPTIONS = ENTITLEMENT_STUDENT_EXCEPTIONS.key;
export const FLAG_ENTITLEMENT_SYNC_SCOPING = ENTITLEMENT_SYNC_SCOPING.key;
